/**
 * @param  {string} type    // 'GET', 'POST', 'PUT', 'DELETE'
 * @param  {string} url
 * @param  {object} params  // {param1: 'texte', param2 = 123}
 * @param  {object} headers  // {'Content-Type': 'application/json'}
 * @returns {Promise}
 */
export function ajax(
    type: string = 'GET',
    url: string,
    params: any = {},
    headers: any = {}
): Promise<any> {
    return new Promise(function(resolve, reject) {
        const xhr = new XMLHttpRequest();

        xhr.open(type, url, true);

        if (!Object.keys(headers).length) {
            if (type.toLowerCase() == 'post') {
                headers = {
                    'Content-Type': 'application/x-www-form-urlencoded',
                };
            }
        }

        if (Object.keys(headers).length) {
            for (const header in headers) {
                xhr.setRequestHeader(header, headers[header]);
            }
        }

        // event to execute when request has loaded
        xhr.addEventListener(
            'load',
            function() {
                if (xhr.status >= 200 && xhr.status < 400) {
                    resolve(xhr);
                } else {
                    reject(xhr);
                }
            },
            false
        );
        // event to execute when an errors occurs
        xhr.addEventListener(
            'error',
            function(e) {
                reject(xhr);
            },
            false
        );
        xhr.send(params);
    });
}

/**
 * @param   {any} obj
 * @returns {any} obj
 */
export function cloneObject(obj: any) {
    return JSON.parse(JSON.stringify((<any>Object).assign({}, obj)));
}

/**
 * @param  {Function} callback
 * @returns void
 */
export function document_ready(callback: Function): void {
    // Si le document est déjà chargé
    if (document.readyState != 'loading') {
        callback();
    } else {
        document.addEventListener('DOMContentLoaded', callback());
    }
}

/**
 * Pour faire un forEach sur un Object
 * @param  {Object} obj
 * @param  {Function} callback(key: string, value: any)
 */
export function each(obj: any, callback: Function) {
    Object.keys(obj).forEach((key: string): void => {
        callback(key, obj[key]);
    });
}

/**
 * @param  {string} event_type
 * @param  {Element[]|NodeList} list
 * @returns void
 */
export function event_off(
    event_type: string,
    list: Element[] | NodeList
): void {
    list.forEach((value: Node) => {
        let node = value as HTMLElement;

        node.removeEventListener(event_type, () => {});
    });
}
/**
 * @param  {string} event_type
 * @param  {Element[]|NodeList} list
 * @param  {Function} callback(ev: CustomEvent, node: Element, data: any)
 * @returns void
 */
export function event_on(
    event_type: string,
    list: Element[] | NodeList,
    callback: Function
): void {
    list.forEach((node: Node) => {
        node.addEventListener(event_type, e => {
            const ev = <CustomEvent>e;

            ev.stopPropagation();

            callback(ev, node, ev.detail);
        });
    });
}
/**
 * @param  {HTMLElement} who_emit
 * @param  {string} event_type
 * @param  {any} detail?
 * @returns void
 */
export function event_emit(
    who_emit: HTMLElement,
    event_type: string,
    data?: any
): void {
    let event = new CustomEvent(event_type, { detail: data });
    who_emit.dispatchEvent(event);
}

/**
 * Pour obtenir la position d'un élément par rapport au 0,0 du document
 * @param  {HTMLElement} element
 * @returns ElementPosition
 */
export function getElementOffsetPosition(
    element: HTMLElement
): ElementPosition {
    let de: HTMLElement = document.documentElement;
    let box: DOMRect = element.getBoundingClientRect();
    let top: number = box.top + window.pageYOffset - de.clientTop;
    let left: number = box.left + window.pageXOffset - de.clientLeft;

    return { top: top, left: left };
}

/**
 * @param  {HTMLElement} element
 * @returns {number} index
 */
export function getIndex(element: HTMLElement): number {
    if (!element) return -1;

    let index = 0;

    do {
        index++;
    } while ((element = <HTMLElement>element.previousElementSibling));

    return index;
}

/**
 * Pour checker si un element est à l'extérieur de l'écran
 * @param  {HTMLElement} element
 * @returns boolean
 */
export function isInViewport(element: HTMLElement): boolean {
    var elementTop = getElementOffsetPosition(element).top;
    var elementBottom: number = elementTop + element.offsetHeight;

    var viewportTop: number = window.scrollY;
    var viewportBottom: number = viewportTop + window.innerHeight;

    return elementBottom > viewportTop && elementTop < viewportBottom;
}
export function isVisible(
    element: HTMLElement,
    threshold: number = 0,
    mode: string = 'visible'
): boolean {
    if (
        element.offsetHeight == 0 ||
        element.offsetWidth == 0 ||
        element.style.display == 'none'
    ) {
        return false;
    }

    threshold = threshold || 0;
    mode = mode || 'visible';

    var rect = element.getBoundingClientRect();
    var viewHeight = Math.max(
        document.documentElement.clientHeight,
        window.innerHeight
    );
    var above = rect.bottom - threshold < 0;
    var below = rect.top - viewHeight + threshold >= 0;

    return mode === 'above'
        ? above
        : mode === 'below'
        ? below
        : !above && !below;
}

/**
 * @param  {string} prefix
 * @returns {string} xxx-123456
 */
export function randomID(prefix: string): string {
    return prefix + Math.round(Math.random() * 1000000);
}

/**
 * Pour attribuer la hauteur la + haute à tous les éléments sélectionnés
 * @param  {string} selector
 * @returns void
 */
export function sameHeight(elements: Element[] | NodeList): void {
    let hauteur_max = 0;

    // Recherche de la hauteur la plus haute
    elements.forEach((value: Node): void => {
        const element = <HTMLElement>value;

        // Reset height
        element.style.height = 'auto';

        const hauteur = element.offsetHeight;

        if (hauteur > hauteur_max) {
            hauteur_max = hauteur;
        }
    });

    // Attribution de la hauteur max à tous les éléments
    elements.forEach((value: Node): void => {
        const element = <HTMLElement>value;

        element.style.height = hauteur_max.toString() + 'px';
    });
}

/**
 * @param  {any} data    // example {param1: 'test', param2 = 5}
 */
export function stringifyParams(data: any) {
    const params = new URLSearchParams(data);
    return params.toString();
}

/**
 * Pour envelopper un HTMLElement avec un autre HTMLElement
 * @param  {HTMLElement} child
 * @param  {HTMLElement} wrapper
 * @returns void
 */
export function wrap(wrapped: HTMLElement, wrapper: HTMLElement | null): void {
    if (wrapped == null) {
        return;
    }
    wrapper = wrapper || document.createElement('div');
    // insert wrapper Before wrapped
    const parent = wrapped.parentNode as HTMLElement;
    if (parent != null) {
        parent.insertBefore(wrapper, wrapped);
        wrapper.appendChild(wrapped);
    }
}

/**
 * Pour envelopper l'innerHTML d'un HTMLElement avec un autre HTMLElement
 * @param  {HTMLElement} parent
 * @param  {HTMLElement} wrapper
 * @returns void
 */
export function wrapInner(parent: HTMLElement, wrapper: HTMLElement): void {
    let first_content = parent.innerHTML;
    wrapper.innerHTML = first_content;
    parent.innerHTML = wrapper.outerHTML;
}

export default null;
